<template>
  <aside v-if="hasChapters" class="guest-chapters-panel" data-testid="guest-chapters-side-panel">
    <!-- Header (clickable on mobile to toggle) -->
    <div class="guest-chapters-panel__header" @click="toggleMobileCollapse">
      <Icon name="general.list" size="base" class="text-zinc-500 dark:text-zinc-400" />
      <span>Chapters</span>
      <span class="guest-chapters-panel__count">{{ chapterCount }}</span>
      <Icon :name="isMobileCollapsed ? 'general.chevronDown' : 'general.chevronUp'" size="xs" class="guest-chapters-panel__toggle-icon" />
    </div>

    <!-- Active chapter preview (mobile collapsed) -->
    <div v-if="isMobileCollapsed && activeChapter" class="guest-chapters-panel__preview">
      <button type="button" class="guest-chapters-panel__button" @click="seekToChapter(activeChapter)">
        <span class="guest-chapters-panel__timestamp">{{ formatTimeCode(activeChapter.timecode) }}</span>
        <span class="guest-chapters-panel__title">{{ activeChapter.title }}</span>
      </button>
    </div>

    <!-- Full chapter list -->
    <ul v-show="!isMobileCollapsed" class="guest-chapters-panel__list">
      <li
        v-for="(chapter, index) in chapters"
        :key="index"
        class="guest-chapters-panel__item group"
        :class="{ 'guest-chapters-panel__item--active': isActiveChapter(chapter) }"
        data-testid="guest-chapters-item"
      >
        <button type="button" class="guest-chapters-panel__button" @click="seekToChapter(chapter)">
          <span class="guest-chapters-panel__timestamp">{{ formatTimeCode(chapter.timecode) }}</span>
          <span class="guest-chapters-panel__title">{{ chapter.title }}</span>
        </button>
        <ButtonClipboard v-bind="getChapterShareConfig(chapter)" class="flex-shrink-0 mr-4" hidden-until-parent-hover data-testid="guest-chapters-share" />
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Icon } from '@/storybook';
import { useMediaQuery } from '@vueuse/core';

import { useChapters } from '@/modules/viewer/composables/useChapters';
import ButtonClipboard from '@/modules/viewer/components/meta_buttons/ButtonClipboard.vue';

const { hasChapters, chapters, isActiveChapter, activeChapter, formatTimeCode, seekToChapter, getChapterShareConfig } = useChapters();

const isMobile = useMediaQuery('(max-width: 1023px)');
const isMobileCollapsed = ref(true);

const chapterCount = computed(() => chapters.value?.length ?? 0);

// On desktop, always expanded
watch(
  isMobile,
  mobile => {
    if (!mobile) {
      isMobileCollapsed.value = false;
    } else {
      isMobileCollapsed.value = true;
    }
  },
  { immediate: true }
);

const toggleMobileCollapse = () => {
  if (isMobile.value) {
    isMobileCollapsed.value = !isMobileCollapsed.value;
  }
};
</script>

<style scoped>
.guest-chapters-panel {
  @apply flex flex-col bg-white dark:bg-zinc-800 sm:rounded-xl sm:border sm:border-zinc-200 sm:dark:border-zinc-700 w-full;
}

.guest-chapters-panel__header {
  @apply flex items-center gap-2 px-4 sm:px-5 pt-4 pb-3 text-base sm:text-lg font-semibold text-zinc-900 dark:text-zinc-100 cursor-pointer lg:cursor-default;
}

.guest-chapters-panel__count {
  @apply text-xs font-medium text-zinc-400 dark:text-zinc-500 lg:hidden;
}

.guest-chapters-panel__toggle-icon {
  @apply text-zinc-400 dark:text-zinc-500 ml-auto lg:hidden;
}

.guest-chapters-panel__preview {
  @apply px-2 pb-2 lg:hidden;
}

.guest-chapters-panel__list {
  @apply flex flex-col px-2 pb-2;
  list-style: none;
  margin: 0;
}

.guest-chapters-panel__item {
  @apply flex items-center gap-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors border-b border-zinc-100 dark:border-zinc-700/50 last:border-b-0;
}

.guest-chapters-panel__item--active {
  @apply bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30;
}

.guest-chapters-panel__item--active .guest-chapters-panel__timestamp {
  @apply text-blue-600 dark:text-blue-400;
}

.guest-chapters-panel__item--active .guest-chapters-panel__title {
  @apply text-blue-700 dark:text-blue-300;
}

.guest-chapters-panel__button {
  @apply flex items-start gap-4 flex-grow text-left min-w-0 px-3 py-3;
}

.guest-chapters-panel__timestamp {
  @apply flex-shrink-0 text-sm font-medium text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-600 px-2 py-1 rounded mt-0.5;
}

.guest-chapters-panel__title {
  @apply text-base text-zinc-800 dark:text-zinc-200 leading-snug;
}
</style>
